import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { QuoteCardType } from 'components/QuoteCard';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useCustomers } from 'hooks/useCustomers';
import { useToast } from 'hooks/useToaster';
import { LTLCustomerStaff } from 'lib/api/getLTLCustomerStaff';
import { LTLExternalQuoteAssignment } from 'lib/api/getLTLQuickQuotes';
import { LaneHistoryResponse, SourceHistory } from 'lib/api/getLaneHistory';
import { LaneHistoryFromServiceResponse } from 'lib/api/getLaneHistoryFromService';
import { QuickQuoteResponse, RouteSegment } from 'lib/api/getQuickQuote';
import { QuickQuoteCustomAccessorial } from 'lib/api/getQuickQuoteCustomAccessorials';
import { IntegrationCore } from 'types/Integration';
import { TMSCustomer } from 'types/Load';
import { Maybe, Undef } from 'types/UtilityTypes';
import { CurrencyOption } from 'types/enums/CurrencyOptions';
import { QuoteChanges } from 'types/suggestions/QuoteSuggestions';
import { GenericCompanySearchableFields } from 'utils/loadInfoAndBuilding';

import { DistanceSource } from '../FTL/RouteDisplaySection';
import { LTLCustomer } from '../types';
import {
  CarrierCostType,
  LTLAccessorial,
  LTLQuote,
  ProfitType,
  QuoteMode,
  SelectedQuoteType,
} from '../types';

export type ExternalQuoteStatus = 'success' | 'failure';

/**
 * QuickQuote Context State Interface
 * Centralizes all state management for the QuickQuote flow
 */
interface QuickQuoteContextState {
  // Quote Response & Cards
  quoteResponse: Maybe<QuickQuoteResponse>;
  setQuoteResponse: (response: Maybe<QuickQuoteResponse>) => void;
  quoteCards: QuoteCardType[];
  setQuoteCards: React.Dispatch<React.SetStateAction<QuoteCardType[]>>;
  quoteCardErrors: string[];
  setQuoteCardErrors: (errors: string[]) => void;
  selectedQuote: Maybe<SelectedQuoteType>;
  setSelectedQuote: (quote: Maybe<SelectedQuoteType>) => void;
  hasUserSelectedCard: boolean;
  setHasUserSelectedCard: (selected: boolean) => void;
  quoteNotConfident: boolean;
  setQuoteNotConfident: React.Dispatch<React.SetStateAction<boolean>>;

  // LTL State
  ltlQuotes: LTLQuote[];
  setLTLQuotes: (quotes: LTLQuote[]) => void;
  selectedLTLQuotes: LTLQuote[];
  setSelectedLTLQuotes: React.Dispatch<React.SetStateAction<LTLQuote[]>>;
  isLoadingLTLQuotes: boolean;
  setIsLoadingLTLQuotes: (loading: boolean) => void;
  ltlQuoteFetchError: boolean;
  setLtlQuoteFetchError: (error: boolean) => void;
  ltlAccessorials: LTLAccessorial[];
  setLtlAccessorials: (accessorials: LTLAccessorial[]) => void;
  isLoadingAccessorials: boolean;
  setIsLoadingAccessorials: (loading: boolean) => void;
  /** Custom (Non-TMS) accessorials stored in DB (selected as MultiSelect options). */
  availableCustomAccessorials: QuickQuoteCustomAccessorial[];
  setavailableCustomAccessorials: React.Dispatch<
    React.SetStateAction<QuickQuoteCustomAccessorial[]>
  >;
  ltlExternalQuoteURL: Maybe<string>;
  setLtlExternalQuoteURL: (url: Maybe<string>) => void;
  ltlExternalQuoteID: Maybe<string>;
  setLtlExternalQuoteID: (id: Maybe<string>) => void;
  ltlExternalQuoteSource: Maybe<string>;
  setLtlExternalQuoteSource: (source: Maybe<string>) => void;
  ltlExternalQuoteLabel: Maybe<string>;
  setLtlExternalQuoteLabel: (label: Maybe<string>) => void;
  ltlExternalQuoteStatus: Maybe<ExternalQuoteStatus>;
  setLtlExternalQuoteStatus: (status: Maybe<ExternalQuoteStatus>) => void;
  ltlExternalQuoteErrorMessage: Maybe<string>;
  setLtlExternalQuoteErrorMessage: (message: Maybe<string>) => void;
  ltlExternalQuoteAssignment: Maybe<LTLExternalQuoteAssignment>;
  setLtlExternalQuoteAssignment: (
    assignment: Maybe<LTLExternalQuoteAssignment>
  ) => void;
  ltlCustomer: Maybe<LTLCustomer>;
  setLtlCustomer: (customer: Maybe<LTLCustomer>) => void;
  ltlCustomerStaff: Maybe<LTLCustomerStaff>;
  setLtlCustomerStaff: (staff: Maybe<LTLCustomerStaff>) => void;

  // Lane History State
  laneHistory: Maybe<LaneHistoryResponse>;
  setLaneHistory: (history: Maybe<LaneHistoryResponse>) => void;
  isLoadingLaneHistory: boolean;
  setIsLoadingLaneHistory: (loading: boolean) => void;
  tmsLaneHistory: Maybe<SourceHistory[]>;
  setTMSLaneHistory: (history: Maybe<SourceHistory[]>) => void;
  tmsLaneHistorySelectedTierIndex: Maybe<number>;
  setTmsLaneHistorySelectedTierIndex: (index: Maybe<number>) => void;
  laneHistoryFromService: Maybe<LaneHistoryFromServiceResponse>;
  setLaneHistoryFromService: (
    history: Maybe<LaneHistoryFromServiceResponse>
  ) => void;
  isLoadingLaneHistoryFromService: boolean;
  setIsLoadingLaneHistoryFromService: (loading: boolean) => void;

  // Pricing State
  carrierCost: number;
  setCarrierCost: React.Dispatch<React.SetStateAction<number>>;
  carrierCostType: CarrierCostType;
  setCarrierCostType: React.Dispatch<React.SetStateAction<CarrierCostType>>;
  profit: number;
  setProfit: React.Dispatch<React.SetStateAction<number>>;
  profitType: ProfitType;
  setProfitType: React.Dispatch<React.SetStateAction<ProfitType>>;
  finalPrice: Maybe<number>;
  setFinalPrice: React.Dispatch<React.SetStateAction<Maybe<number>>>;
  finalFlatPrice: Maybe<number>;
  setFinalFlatPrice: React.Dispatch<React.SetStateAction<Maybe<number>>>;
  finalPriceFormat: CarrierCostType | 'Both';
  setFinalPriceFormat: React.Dispatch<
    React.SetStateAction<CarrierCostType | 'Both'>
  >;

  // Route & Distance State
  maxDistance: number;
  setMaxDistance: (distance: number) => void;
  distanceSource: Maybe<DistanceSource>;
  setDistanceSource: (source: Maybe<DistanceSource>) => void;
  routeDetails: Maybe<{ source: DistanceSource; legs: RouteSegment[] }>;
  setRouteDetails: (
    details: Maybe<{ source: DistanceSource; legs: RouteSegment[] }>
  ) => void;
  stopFee: number;
  setStopFee: (fee: number) => void;
  intermediateStopCount: number;
  setIntermediateStopCount: (count: number) => void;
  lastAppliedStopFeeRef: React.MutableRefObject<number>;

  // DAT State
  datFuelSurcharge: Maybe<number>;
  setDATFuelSurcharge: (surcharge: Maybe<number>) => void;
  hasGrantedDATPermissions: Maybe<boolean>;
  setHasGrantedDATPermissions: React.Dispatch<
    React.SetStateAction<Maybe<boolean>>
  >;
  isLoadingDAT: boolean;
  setIsLoadingDAT: (loading: boolean) => void;

  // Draft Response State
  draftResponse: string;
  setDraftResponse: (response: string) => void;
  hasCopiedDraftResponse: boolean;
  setHasCopiedDraftResponse: (copied: boolean) => void;
  userEditedDraft: boolean;
  setUserEditedDraft: (edited: boolean) => void;
  loadingDraftReply: boolean;
  setLoadingDraftReply: (loading: boolean) => void;
  /** When true, FTL draft rate sync is paused so Jodit does not steal focus from bill-rate edit. */
  isBillRateEditSessionActive: boolean;
  setIsBillRateEditSessionActive: (active: boolean) => void;

  // TMS & Customer State
  customers: Maybe<TMSCustomer[]>;
  customersLoading: boolean;
  tmsTenant: Maybe<string>;
  handleRefreshCustomers: () => void;
  handleResetCustomerSearch: () => void;
  ensureCustomerByExternalTmsId: (externalTMSID: string) => Promise<void>;
  handleCustomerSearch: (
    searchParam: GenericCompanySearchableFields,
    searchValue: string
  ) => Promise<any[]>;

  // Quote Submission State
  isSubmitToTMS: boolean;
  setIsSubmitToTMS: (submit: boolean) => void;
  createdQuoteId: Maybe<string>;
  setCreatedQuoteId: (id: Maybe<string>) => void;
  createdQuoteInternalId: Maybe<number>;
  setCreatedQuoteInternalId: (id: Maybe<number>) => void;
  hasThirdPartyQuoteURLs: boolean;
  setHasThirdPartyQuoteURLs: React.Dispatch<React.SetStateAction<boolean>>;
  parentQuoteRequestId: number;
  setParentQuoteRequestId: (id: number) => void;
  greenscreensQuoteID: Maybe<string>;
  setGreenscreensQuoteID: React.Dispatch<React.SetStateAction<Maybe<string>>>;
  submitQuoteLoading: boolean;
  setSubmitQuoteLoading: (loading: boolean) => void;

  // UI State
  quoteMode: QuoteMode;
  setQuoteMode: (mode: QuoteMode) => void;
  quoteCurrency: CurrencyOption;
  setQuoteCurrency: (currency: CurrencyOption) => void;
  scrollResultsIntoViewRef: React.RefObject<Maybe<HTMLDivElement>>;
}

const QuickQuoteContext =
  createContext<Undef<QuickQuoteContextState>>(undefined);

export { QuickQuoteContext };

/**
 * Custom hook to access QuickQuote context
 * Throws error if used outside of QuickQuoteProvider
 */
export const useQuickQuoteContext = () => {
  const context = useContext(QuickQuoteContext);
  if (!context) {
    throw new Error(
      'useQuickQuoteContext must be used within a QuickQuoteProvider'
    );
  }
  return context;
};

interface QuickQuoteProviderProps {
  children: ReactNode;
  tmsIntegrations: Maybe<IntegrationCore[]>;
  ltlTMSIntegrations: Maybe<IntegrationCore[]>;
  defaultProfitType?: ProfitType;
  defaultProfit?: number;
  defaultQuoteMode?: QuoteMode;
}

// Provider component that centralizes state management for Quick Quote
export const QuickQuoteProvider: React.FC<QuickQuoteProviderProps> = ({
  children,
  tmsIntegrations,
  ltlTMSIntegrations,
  defaultProfitType = ProfitType.Percentage,
  defaultProfit = 10,
  defaultQuoteMode = QuoteMode.TL,
}) => {
  const { toast } = useToast();
  const {
    currentState: { clickedSuggestion, inboxEmailAddress },
    setCurrentState,
  } = useContext(SidebarStateContext);
  const suggestedCustomerId = (clickedSuggestion?.suggested as QuoteChanges)
    ?.customerExternalTMSID;

  const {
    customers,
    isLoading: customersLoading,
    tmsTenant,
    refreshCustomers: handleRefreshCustomers,
    resetCustomerSearch: handleResetCustomerSearch,
    ensureCustomerByExternalTmsId,
    customerSearch: handleCustomerSearch,
  } = useCustomers(tmsIntegrations, { toast, suggestedCustomerId });

  const {
    customers: ltlCustomers,
    isLoading: ltlCustomersLoading,
    tmsTenant: ltlTmsTenant,
    refreshCustomers: handleLtlRefreshCustomers,
    resetCustomerSearch: handleLtlResetCustomerSearch,
    ensureCustomerByExternalTmsId: ensureLtlCustomerByExternalTmsId,
    customerSearch: handleLtlCustomerSearch,
  } = useCustomers(ltlTMSIntegrations, {
    toast,
    suggestedCustomerId,
    isLTL: true,
  });

  // Quote Response & Cards
  const [quoteResponse, setQuoteResponse] =
    useState<Maybe<QuickQuoteResponse>>(null);
  const [quoteCards, setQuoteCards] = useState<QuoteCardType[]>([]);
  const [quoteCardErrors, setQuoteCardErrors] = useState<string[]>([]);
  const [selectedQuote, setSelectedQuote] = useState<Maybe<SelectedQuoteType>>(
    SelectedQuoteType.GS_NETWORK
  );
  const [hasUserSelectedCard, setHasUserSelectedCard] = useState(false);
  const [quoteNotConfident, setQuoteNotConfident] = useState(false);

  // LTL State
  const [ltlQuotes, setLTLQuotes] = useState<LTLQuote[]>([]);
  const [selectedLTLQuotes, setSelectedLTLQuotes] = useState<LTLQuote[]>([]);
  const [isLoadingLTLQuotes, setIsLoadingLTLQuotes] = useState(false);
  const [ltlQuoteFetchError, setLtlQuoteFetchError] = useState(false);
  const [ltlAccessorials, setLtlAccessorials] = useState<LTLAccessorial[]>([]);
  const [isLoadingAccessorials, setIsLoadingAccessorials] = useState(false);
  const [availableCustomAccessorials, setavailableCustomAccessorials] =
    useState<QuickQuoteCustomAccessorial[]>([]);
  const [ltlExternalQuoteURL, setLtlExternalQuoteURL] =
    useState<Maybe<string>>(null);
  const [ltlExternalQuoteID, setLtlExternalQuoteID] =
    useState<Maybe<string>>(null);
  const [ltlExternalQuoteSource, setLtlExternalQuoteSource] =
    useState<Maybe<string>>(null);
  const [ltlExternalQuoteLabel, setLtlExternalQuoteLabel] =
    useState<Maybe<string>>(null);
  const [ltlExternalQuoteStatus, setLtlExternalQuoteStatus] =
    useState<Maybe<ExternalQuoteStatus>>(null);
  const [ltlExternalQuoteErrorMessage, setLtlExternalQuoteErrorMessage] =
    useState<Maybe<string>>(null);
  const [ltlExternalQuoteAssignment, setLtlExternalQuoteAssignment] =
    useState<Maybe<LTLExternalQuoteAssignment>>(null);
  const [ltlCustomer, setLtlCustomer] = useState<Maybe<LTLCustomer>>(null);
  const [ltlCustomerStaff, setLtlCustomerStaff] =
    useState<Maybe<LTLCustomerStaff>>(null);

  // Lane History State
  const [laneHistory, setLaneHistory] =
    useState<Maybe<LaneHistoryResponse>>(null);
  const [isLoadingLaneHistory, setIsLoadingLaneHistory] = useState(false);
  const [tmsLaneHistory, setTMSLaneHistory] =
    useState<Maybe<SourceHistory[]>>(null);
  const [tmsLaneHistorySelectedTierIndex, setTmsLaneHistorySelectedTierIndex] =
    useState<Maybe<number>>(null);
  const [laneHistoryFromService, setLaneHistoryFromService] =
    useState<Maybe<LaneHistoryFromServiceResponse>>(null);
  const [isLoadingLaneHistoryFromService, setIsLoadingLaneHistoryFromService] =
    useState(false);

  // Pricing State
  const [carrierCost, setCarrierCost] = useState(0);
  const [carrierCostType, setCarrierCostType] = useState<CarrierCostType>(
    CarrierCostType.Flat
  );
  const [profit, setProfit] = useState(defaultProfit);
  const [profitType, setProfitType] = useState<ProfitType>(defaultProfitType);
  const syncedInboxForDefaultsRef = useRef<string | null>(null);
  const marginBeforeInboxHydrationRef = useRef({
    profit: defaultProfit,
    profitType: defaultProfitType,
  });

  useEffect(() => {
    if (!inboxEmailAddress) {
      return;
    }

    const previousInbox = syncedInboxForDefaultsRef.current;
    syncedInboxForDefaultsRef.current = inboxEmailAddress;

    // Inbox hydrates async (AuthProvider). Apply TQL/service defaults once it arrives,
    // but only if the user has not changed margin while the inbox was still empty.
    if (previousInbox === null || previousInbox === '') {
      const snapshot = marginBeforeInboxHydrationRef.current;
      if (profit === snapshot.profit && profitType === snapshot.profitType) {
        setProfit(defaultProfit);
        setProfitType(defaultProfitType);
      }
    }
  }, [inboxEmailAddress, defaultProfit, defaultProfitType, profit, profitType]);

  const [finalPrice, setFinalPrice] = useState<Maybe<number>>(null);
  const [finalFlatPrice, setFinalFlatPrice] = useState<Maybe<number>>(null);
  const [finalPriceFormat, setFinalPriceFormat] = useState<
    CarrierCostType | 'Both'
  >(CarrierCostType.Flat);

  // Route & Distance State
  const [maxDistance, setMaxDistance] = useState(0);
  const [distanceSource, setDistanceSource] =
    useState<Maybe<DistanceSource>>(null);
  const [routeDetails, setRouteDetails] =
    useState<Maybe<{ source: DistanceSource; legs: RouteSegment[] }>>(null);
  const [stopFee, setStopFee] = useState(0);
  const [intermediateStopCount, setIntermediateStopCount] = useState(0);
  const lastAppliedStopFeeRef = useRef<number>(0);

  // DAT State
  const [datFuelSurcharge, setDATFuelSurcharge] = useState<Maybe<number>>(null);
  const [hasGrantedDATPermissions, setHasGrantedDATPermissions] =
    useState<Maybe<boolean>>(null);
  const [isLoadingDAT, setIsLoadingDAT] = useState(false);

  // Draft Response State
  const [draftResponse, setDraftResponse] = useState('');
  const [hasCopiedDraftResponse, setHasCopiedDraftResponse] = useState(false);
  const [userEditedDraft, setUserEditedDraft] = useState(false);
  const [loadingDraftReply, setLoadingDraftReply] = useState(false);
  const [isBillRateEditSessionActive, setIsBillRateEditSessionActive] =
    useState(false);

  // Quote Submission State
  const [isSubmitToTMS, setIsSubmitToTMS] = useState(true);
  const [createdQuoteId, setCreatedQuoteId] = useState<Maybe<string>>(null);
  const [createdQuoteInternalId, setCreatedQuoteInternalId] =
    useState<Maybe<number>>(null);
  const [hasThirdPartyQuoteURLs, setHasThirdPartyQuoteURLs] = useState(false);
  const [parentQuoteRequestId, setParentQuoteRequestIdState] = useState(0);
  const setParentQuoteRequestId = useCallback(
    (id: number) => {
      setParentQuoteRequestIdState(id);

      if (id > 0) {
        setCurrentState((prev) =>
          prev.latestQuoteRequestId === id
            ? prev
            : { ...prev, latestQuoteRequestId: id }
        );
      }
    },
    [setCurrentState]
  );
  const [greenscreensQuoteID, setGreenscreensQuoteID] =
    useState<Maybe<string>>(null);
  const [submitQuoteLoading, setSubmitQuoteLoading] = useState(false);

  // UI State
  const [quoteMode, setQuoteMode] = useState<QuoteMode>(defaultQuoteMode);
  const [quoteCurrency, setQuoteCurrency] = useState<CurrencyOption>(
    CurrencyOption.USD
  );
  const scrollResultsIntoViewRef = useRef<HTMLDivElement>(null);

  // Expose customer data based on current quote mode (TL vs LTL)
  const isLTL = quoteMode === QuoteMode.LTL;
  const customersByMode = isLTL ? ltlCustomers : customers;
  const customersLoadingByMode = isLTL ? ltlCustomersLoading : customersLoading;
  const tmsTenantByMode = isLTL ? ltlTmsTenant : tmsTenant;
  const handleRefreshCustomersByMode = isLTL
    ? handleLtlRefreshCustomers
    : handleRefreshCustomers;
  const handleResetCustomerSearchByMode = isLTL
    ? handleLtlResetCustomerSearch
    : handleResetCustomerSearch;
  const ensureCustomerByExternalTmsIdByMode = isLTL
    ? ensureLtlCustomerByExternalTmsId
    : ensureCustomerByExternalTmsId;
  const handleCustomerSearchByMode = isLTL
    ? handleLtlCustomerSearch
    : handleCustomerSearch;

  const value: QuickQuoteContextState = {
    // Quote Response & Cards
    quoteResponse,
    setQuoteResponse,
    quoteCards,
    setQuoteCards,
    quoteCardErrors,
    setQuoteCardErrors,
    selectedQuote,
    setSelectedQuote,
    hasUserSelectedCard,
    setHasUserSelectedCard,
    quoteNotConfident,
    setQuoteNotConfident,

    // LTL State
    ltlQuotes,
    setLTLQuotes,
    selectedLTLQuotes,
    setSelectedLTLQuotes,
    isLoadingLTLQuotes,
    setIsLoadingLTLQuotes,
    ltlQuoteFetchError,
    setLtlQuoteFetchError,
    ltlCustomer,
    setLtlCustomer,
    ltlCustomerStaff,
    setLtlCustomerStaff,
    ltlAccessorials,
    setLtlAccessorials,
    isLoadingAccessorials,
    setIsLoadingAccessorials,
    availableCustomAccessorials,
    setavailableCustomAccessorials,
    ltlExternalQuoteURL,
    setLtlExternalQuoteURL,
    ltlExternalQuoteID,
    setLtlExternalQuoteID,
    ltlExternalQuoteSource,
    setLtlExternalQuoteSource,
    ltlExternalQuoteLabel,
    setLtlExternalQuoteLabel,
    ltlExternalQuoteStatus,
    setLtlExternalQuoteStatus,
    ltlExternalQuoteErrorMessage,
    setLtlExternalQuoteErrorMessage,
    ltlExternalQuoteAssignment,
    setLtlExternalQuoteAssignment,

    // Lane History State
    laneHistory,
    setLaneHistory,
    isLoadingLaneHistory,
    setIsLoadingLaneHistory,
    tmsLaneHistory,
    setTMSLaneHistory,
    tmsLaneHistorySelectedTierIndex,
    setTmsLaneHistorySelectedTierIndex,
    laneHistoryFromService,
    setLaneHistoryFromService,
    isLoadingLaneHistoryFromService,
    setIsLoadingLaneHistoryFromService,

    // Pricing State
    carrierCost,
    setCarrierCost,
    carrierCostType,
    setCarrierCostType,
    profit,
    setProfit,
    profitType,
    setProfitType,
    finalPrice,
    setFinalPrice,
    finalFlatPrice,
    setFinalFlatPrice,
    finalPriceFormat,
    setFinalPriceFormat,

    // Route & Distance State
    maxDistance,
    setMaxDistance,
    distanceSource,
    setDistanceSource,
    routeDetails,
    setRouteDetails,
    stopFee,
    setStopFee,
    intermediateStopCount,
    setIntermediateStopCount,
    lastAppliedStopFeeRef,

    // DAT State
    datFuelSurcharge,
    setDATFuelSurcharge,
    hasGrantedDATPermissions,
    setHasGrantedDATPermissions,
    isLoadingDAT,
    setIsLoadingDAT,

    // Draft Response State
    draftResponse,
    setDraftResponse,
    hasCopiedDraftResponse,
    setHasCopiedDraftResponse,
    userEditedDraft,
    setUserEditedDraft,
    loadingDraftReply,
    setLoadingDraftReply,
    isBillRateEditSessionActive,
    setIsBillRateEditSessionActive,

    // TMS & Customer State (switches by quoteMode: TL vs LTL)
    customers: customersByMode,
    customersLoading: customersLoadingByMode,
    tmsTenant: tmsTenantByMode,
    handleRefreshCustomers: handleRefreshCustomersByMode,
    handleResetCustomerSearch: handleResetCustomerSearchByMode,
    ensureCustomerByExternalTmsId: ensureCustomerByExternalTmsIdByMode,
    handleCustomerSearch: handleCustomerSearchByMode,

    // Quote Submission State
    isSubmitToTMS,
    setIsSubmitToTMS,
    createdQuoteId,
    setCreatedQuoteId,
    createdQuoteInternalId,
    setCreatedQuoteInternalId,
    hasThirdPartyQuoteURLs,
    setHasThirdPartyQuoteURLs,
    parentQuoteRequestId,
    setParentQuoteRequestId,
    greenscreensQuoteID,
    setGreenscreensQuoteID,
    submitQuoteLoading,
    setSubmitQuoteLoading,

    // UI State
    quoteMode,
    setQuoteMode,
    quoteCurrency,
    setQuoteCurrency,
    scrollResultsIntoViewRef,
  };

  return (
    <QuickQuoteContext.Provider value={value}>
      {children}
    </QuickQuoteContext.Provider>
  );
};
